/**
 * RequireCapability — Phase B tier-gate wrapper.
 *
 * Wraps a page that requires a specific tier capability. While the
 * capability manifest is loading, shows a centred spinner. If the user
 * lacks the capability, renders a friendly upgrade CTA rather than the
 * silent 403 they'd get from poking the API directly. Owner tier
 * always renders the children unconditionally.
 *
 * Pairs with the backend's `require_capability` route guard so a
 * customer can't bypass this — the server returns 403 for the same
 * inputs.
 */
import { useEffect } from 'react'
import { Lock, Sparkles, ShieldCheck } from 'lucide-react'

import { useCapabilities } from '@/hooks/useCapabilities'

export type CapabilityName = 'scenarios' | 'recommendations' | 'auto_execute'

interface RequireCapabilityProps {
  capability: CapabilityName
  children: React.ReactNode
  /** Override the default copy shown when access is denied. */
  upgradeTitle?: string
  upgradeBody?: string
}

const DEFAULT_COPY: Record<CapabilityName, { title: string; body: string; tier: string; price: string }> = {
  scenarios: {
    title: 'Scenarios is a Planner-tier feature',
    body: 'The full scenario workspace — Monte Carlo, drawdown order, jurisdiction comparison, scenario stacker, what-if levers — comes with the Planner tier. The free tier covers basic tracking only.',
    tier: 'Planner',
    price: '£20 / mo',
  },
  recommendations: {
    title: 'Recommendations is an Advised-tier feature',
    body: 'Personal recommendations are reviewed and approved by FCA-authorised advisers at our Principal partner firm before delivery. That review pipeline ships with the Advised tier.',
    tier: 'Advised',
    price: '£75 / mo',
  },
  auto_execute: {
    title: 'Auto-execution is an Advised-tier feature',
    body: 'Pushing trades through your broker requires the Advised tier so a human-in-the-loop AR adviser approves the trade before it executes. Free and Planner tiers see the queue but cannot approve or execute trades.',
    tier: 'Advised',
    price: '£75 / mo',
  },
}

export default function RequireCapability({
  capability,
  children,
  upgradeTitle,
  upgradeBody,
}: RequireCapabilityProps) {
  const capabilities = useCapabilities((s) => s.capabilities)
  const loading = useCapabilities((s) => s.loading)
  const fetch = useCapabilities((s) => s.fetch)

  // Self-fetch if Shell hasn't already (defensive — Shell.tsx fetches
  // on mount, but a deep-link straight to a gated page might race).
  useEffect(() => {
    if (!capabilities && !loading) {
      void fetch()
    }
  }, [capabilities, loading, fetch])

  // While loading, show a spinner — closed-fail until we know.
  if (!capabilities) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="sr-only">Loading capabilities…</span>
        <div
          className="w-8 h-8 border-2 border-blue/30 border-t-blue rounded-full animate-spin"
          aria-hidden
        />
      </div>
    )
  }

  // Owner tier bypasses every gate (Path C invariant).
  if (capabilities.is_owner) {
    return <>{children}</>
  }

  const allowed =
    capability === 'scenarios' ? capabilities.can_see_scenarios :
    capability === 'recommendations' ? capabilities.can_see_recommendations :
    capability === 'auto_execute' ? capabilities.can_auto_execute :
    false

  if (allowed) {
    return <>{children}</>
  }

  const copy = DEFAULT_COPY[capability]
  return (
    <div className="flex items-center justify-center py-10">
      <div className="max-w-lg w-full card p-6 space-y-4 text-center">
        <div className="flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-amber/15 border border-amber/40 flex items-center justify-center">
            <Lock size={20} className="text-amber" aria-hidden />
          </div>
        </div>
        <h2 className="text-lg font-semibold text-text-primary">
          {upgradeTitle ?? copy.title}
        </h2>
        <p className="text-sm text-text-secondary leading-relaxed">
          {upgradeBody ?? copy.body}
        </p>
        <div className="flex items-center justify-center gap-2 py-2">
          <span className="text-xs uppercase tracking-wider text-text-muted">Your tier</span>
          <span className="px-2 py-0.5 rounded bg-surface-card border border-border text-xs text-text-secondary capitalize">
            {capabilities.tier || 'free'}
          </span>
          <span className="text-text-muted">→</span>
          <span className="px-2 py-0.5 rounded bg-blue/10 border border-blue/40 text-xs text-blue-light flex items-center gap-1">
            <Sparkles size={10} aria-hidden /> {copy.tier}
          </span>
          <span className="text-xs text-text-muted">({copy.price})</span>
        </div>
        <p className="text-[11px] text-text-muted leading-relaxed border-t border-border pt-3 flex items-center justify-center gap-1.5">
          <ShieldCheck size={11} aria-hidden />
          The same gate is enforced server-side. You can&apos;t accidentally see
          gated data even with this UI bypassed.
        </p>
      </div>
    </div>
  )
}
